import React, { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { useStripe } from "../hooks/useStripe";
import {
  CardElement,
  useStripe as useStripeElement,
  useElements,
} from "@stripe/react-stripe-js";
import Toast from "./Toast";
import "./EventCheckout.css";

/**
 * EventCheckout Component
 * Handles event ticket purchases with Stripe payment
 * Features:
 * - Ticket selection and quantity
 * - Price breakdown (tickets + platform fee)
 * - Card payment with Stripe
 * - Order confirmation
 */
export default function EventCheckout({ eventId, eventData, ticketPrice, onClose, onSuccess }) {
  const { user, profile } = useAuth();
  const stripe = useStripeElement();
  const elements = useElements();
  const {
    createEventTicketPayment,
    completeEventTicketPurchase,
    loading: stripeLoading,
    error: stripeError,
    clearError,
  } = useStripe();

  const [quantity, setQuantity] = useState(1);
  const [toast, setToast] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // pending, succeeded, failed
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // Calculate amounts
  const ticketSubtotal = Math.round(ticketPrice * quantity);
  const platformFee = Math.round(ticketSubtotal * 0.2); // 20% platform fee
  const organizerAmount = ticketSubtotal - platformFee;
  const totalAmount = ticketSubtotal; // Platform fee is included in total

  // Handle payment submission
  const handlePayment = async (e) => {
    e.preventDefault();

    if (!agreeToTerms) {
      setToast({ message: "Please agree to the terms to continue", type: "error" });
      return;
    }

    if (!stripe || !elements) {
      setToast({ message: "Stripe not loaded", type: "error" });
      return;
    }

    if (quantity < 1) {
      setToast({ message: "Please select at least 1 ticket", type: "error" });
      return;
    }

    try {
      setProcessing(true);
      setPaymentStatus("pending");
      clearError();

      // Step 1: Create payment intent in Cloud Function
      const paymentData = await createEventTicketPayment(
        eventId, 
        ticketPrice, 
        quantity,
        eventData?.eventName || "Event Ticket Purchase"
      );
      const { clientSecret, paymentIntentId } = paymentData;

      // Step 2: Confirm payment with Stripe
      const cardElement = elements.getElement(CardElement);
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: profile?.name || "",
            email: user?.email || "",
          },
        },
      });

      if (error) {
        setPaymentStatus("failed");
        setToast({ message: error.message, type: "error" });
        setProcessing(false);
        return;
      }

      if (paymentIntent.status === "succeeded") {
        // Step 3: Complete the purchase
        const result = await completeEventTicketPurchase(paymentIntentId, eventId, quantity);

        setPaymentStatus("succeeded");
        setToast({
          message: `Successfully purchased ${quantity} ticket(s)!`,
          type: "success",
        });

        // Wait a moment then call success callback
        setTimeout(() => {
          if (onSuccess) {
            onSuccess({ paymentIntentId, quantity, confirmationNumber: result?.confirmationNumber });
          }
        }, 1500);
      }
    } catch (error) {
      setPaymentStatus("failed");
      setToast({ message: error.message || "Payment failed", type: "error" });
      setProcessing(false);
    }
  };

  return (
    <div className="event-checkout">
      <div className="checkout-header">
        <h2>Purchase Event Tickets</h2>
        <p className="event-name">{eventData?.eventName || "Event"}</p>
      </div>

      <form onSubmit={handlePayment} className="checkout-form">
        {/* Ticket Selection */}
        <div className="form-section">
          <h3>Select Quantity</h3>
          <div className="quantity-selector">
            <button
              type="button"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={processing || quantity === 1}
              className="qty-btn"
            >
              −
            </button>
            <input
              type="number"
              min="1"
              max="10"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              disabled={processing}
              className="qty-input"
            />
            <button
              type="button"
              onClick={() => setQuantity(Math.min(10, quantity + 1))}
              disabled={processing || quantity === 10}
              className="qty-btn"
            >
              +
            </button>
          </div>
          <p className="qty-hint">Max 10 tickets per order</p>
        </div>

        {/* Price Breakdown */}
        <div className="form-section price-breakdown">
          <h3>Price Breakdown</h3>
          <div className="price-row">
            <span className="label">Ticket Price × {quantity}</span>
            <span className="value">${(ticketSubtotal / 100).toFixed(2)}</span>
          </div>
          <div className="price-row breakdown-item">
            <span className="label small">To Event Organizer (80%)</span>
            <span className="value small">${(organizerAmount / 100).toFixed(2)}</span>
          </div>
          <div className="price-row breakdown-item">
            <span className="label small">Platform Fee (20%)</span>
            <span className="value small">${(platformFee / 100).toFixed(2)}</span>
          </div>
          <div className="price-row total">
            <span className="label">Total</span>
            <span className="value total-amount">${(totalAmount / 100).toFixed(2)}</span>
          </div>
        </div>

        {/* Payment Method */}
        <div className="form-section">
          <h3>Payment Method</h3>
          <div className="card-element-wrapper">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    "::placeholder": { color: "#aab7c4" },
                    fontFamily: 'Segoe UI, Roboto, sans-serif',
                  },
                  invalid: { color: "#fa755a" },
                },
              }}
            />
          </div>
        </div>

        {/* Error Message */}
        {(stripeError || toast?.type === "error") && (
          <div className="error-message">
            ⚠️ {stripeError || toast?.message}
          </div>
        )}

        {/* Terms Agreement */}
        <div className="form-section terms">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
              disabled={processing}
            />
            <span>
              I agree to the terms and conditions and understand that this is a non-refundable purchase
            </span>
          </label>
        </div>

        {/* Submit Button */}
        <div className="form-actions">
          <button
            type="button"
            onClick={onClose}
            disabled={processing}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              processing ||
              stripeLoading ||
              !agreeToTerms ||
              quantity < 1
            }
            className={`btn-primary ${paymentStatus ? `status-${paymentStatus}` : ""}`}
          >
            {processing ? "Processing..." : `Pay $${(totalAmount / 100).toFixed(2)}`}
          </button>
        </div>
      </form>

      {/* Payment Status */}
      {paymentStatus === "succeeded" && (
        <div className="payment-success">
          <div className="success-icon">✓</div>
          <h3>Payment Successful!</h3>
          <p>Your tickets will be delivered to your email shortly.</p>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => {
            if (toast.type !== "error") {
              setToast(null);
            }
          }}
        />
      )}
    </div>
  );
}
